<template>
    <div class="modal-card card cname-validation-modal">
        <div class="card-header">
            <h4 class="card-header-title">
                <i class="fa fa-shield-alt me-1" aria-hidden="true"></i>
                Domain Validation Details
            </h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="card-body cname-body">

            <!-- DNS Settings (traffic routing) -->
            <div v-if="hasDnsSettings" class="cname-section mb-2">
                <div class="cname-section-title text-primary">
                    <i class="fa fa-server me-1" aria-hidden="true"></i>
                    DNS Settings
                </div>
                <p class="cname-section-desc mb-2">
                    Add these records in your DNS for traffic routing.
                </p>
                <div v-if="edgeHostname" class="cname-field mb-2">
                    <label class="cname-field-label">Edge Hostname</label>
                    <div class="input-group cname-input-group">
                        <input class="form-control form-control-sm font-monospace" :value="edgeHostname" readonly />
                        <button type="button" class="btn btn-copy" @click="onCopyText(edgeHostname, 'Edge hostname')"
                            title="Copy">
                            <i class="fa fa-copy" />
                        </button>
                    </div>
                </div>
                <div v-if="showAliasRecord && (aliasRecordLeft || aliasRecordRight)" class="cname-field mb-2">
                    <label class="cname-field-label">ALIAS record</label>
                    <div class="row g-2 align-items-center">
                        <div class="col-12 col-lg-5">
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace"
                                    :value="aliasRecordLeft || '-'" readonly />
                                <button type="button" class="btn btn-copy" :disabled="!aliasRecordLeft"
                                    @click="onCopyText(aliasRecordLeft, 'ALIAS from')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                        <div class="col-12 col-lg-1 text-center cname-arrow">
                            <i class="fa fa-arrow-right d-none d-lg-inline" aria-hidden="true"></i>
                            <i class="fa fa-arrow-down d-lg-none" aria-hidden="true"></i>
                        </div>
                        <div class="col-12 col-lg-6">
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace"
                                    :value="aliasRecordRight || '-'" readonly />
                                <button type="button" class="btn btn-copy" :disabled="!aliasRecordRight"
                                    @click="onCopyText(aliasRecordRight, 'ALIAS to')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showCnameRecord && (cnameRecordLeft || cnameRecordRight)" class="cname-field">
                    <label class="cname-field-label">CNAME record</label>
                    <div class="row g-2 align-items-center">
                        <div class="col-12 col-lg-5">
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace"
                                    :value="cnameRecordLeft || '-'" readonly />
                                <button type="button" class="btn btn-copy" :disabled="!cnameRecordLeft"
                                    @click="onCopyText(cnameRecordLeft, 'CNAME from')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                        <div class="col-12 col-lg-1 text-center cname-arrow">
                            <i class="fa fa-arrow-right d-none d-lg-inline" aria-hidden="true"></i>
                            <i class="fa fa-arrow-down d-lg-none" aria-hidden="true"></i>
                        </div>
                        <div class="col-12 col-lg-6">
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace"
                                    :value="cnameRecordRight || '-'" readonly />
                                <button type="button" class="btn btn-copy" :disabled="!cnameRecordRight"
                                    @click="onCopyText(cnameRecordRight, 'CNAME to')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Validation methods (grouped like DNS Settings) -->
            <div v-if="hasAnyDetails" class="cname-section-validation mb-2">
                <div class="cname-section-title text-success">
                    <i class="fa fa-check-circle me-1" aria-hidden="true"></i>
                    Validation methods
                </div>
                <p class="cname-section-desc mb-2">
                    To complete validation, add <strong>either</strong> the CNAME record <strong>or</strong> the TXT
                    record below to your DNS (you only need one).
                </p>

                <!-- Option 1: CNAME -->
                <div v-if="cnameHost || cnameTarget" class="cname-field mb-2">
                    <div class="cname-option-header">
                        <span class="cname-option-badge">Option 1</span>
                        <span class="cname-option-title">CNAME record</span>
                    </div>
                    <div class="row g-2 align-items-center">
                        <div class="col-12 col-lg-5">
                            <label class="cname-field-label">Host</label>
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace" :value="cnameHost || '-'"
                                    readonly />
                                <button type="button" class="btn btn-copy" :disabled="!cnameHost"
                                    @click="onCopyText(cnameHost, 'CNAME host')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                        <div class="col-12 col-lg-1 text-center cname-arrow">
                            <i class="fa fa-arrow-right d-none d-lg-inline" aria-hidden="true"></i>
                            <i class="fa fa-arrow-down d-lg-none" aria-hidden="true"></i>
                        </div>
                        <div class="col-12 col-lg-6">
                            <label class="cname-field-label">Target</label>
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace" :value="cnameTarget || '-'"
                                    readonly />
                                <button type="button" class="btn btn-copy" :disabled="!cnameTarget"
                                    @click="onCopyText(cnameTarget, 'CNAME target')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Option 2: TXT -->
                <div v-if="txtHost || txtValue" class="cname-field mb-2">
                    <div class="cname-option-header">
                        <span class="cname-option-badge">Option 2</span>
                        <span class="cname-option-title">TXT record</span>
                    </div>
                    <div class="row g-2 align-items-center">
                        <div class="col-12 col-lg-5">
                            <label class="cname-field-label">Host</label>
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace" :value="txtHost || '-'"
                                    readonly />
                                <button type="button" class="btn btn-copy" :disabled="!txtHost"
                                    @click="onCopyText(txtHost, 'TXT host')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                        <div class="col-12 col-lg-1 text-center cname-arrow">
                            <i class="fa fa-arrow-right d-none d-lg-inline" aria-hidden="true"></i>
                            <i class="fa fa-arrow-down d-lg-none" aria-hidden="true"></i>
                        </div>
                        <div class="col-12 col-lg-6">
                            <label class="cname-field-label">Value</label>
                            <div class="input-group cname-input-group">
                                <input class="form-control form-control-sm font-monospace" :value="txtValue || '-'"
                                    readonly />
                                <button type="button" class="btn btn-copy" :disabled="!txtValue"
                                    @click="onCopyText(txtValue, 'TXT value')" title="Copy">
                                    <i class="fa fa-copy" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Validation expiry -->
                <div v-if="validationExpiry" class="cname-expiry mt-2">
                    <span class="cname-expiry-label me-1">Validation expires</span>
                    <span class="cname-expiry-value" :class="{ 'text-danger': isValidationExpired }">{{
                        DateTimeFormat(validationExpiry) }}</span>
                    <button v-if="isValidationExpired" type="button" class="btn btn-refresh btn-refresh-sm ms-2"
                        @click="onRefreshDomain" title="Refresh domain">
                        <i class="fa fa-refresh me-1" aria-hidden="true"></i>
                        Refresh
                    </button>
                </div>
            </div>

            <!-- Empty state -->
            <div v-if="!hasAnyDetails" class="cname-empty">
                <i class="fa fa-inbox mb-1" aria-hidden="true"></i>
                <p class="mb-0 small">No validation details available.</p>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTimeFormat } from "../../utils/commonFunction";

export default {
    name: "CNameValidation",
    props: {
        modalData: {
            type: [Object],
            default: null,
        },
    },
    methods: {
        DateTimeFormat,
        onRefreshDomain() {
            const onRefresh = this.modalData?.onRefresh;
            if (typeof onRefresh === 'function') onRefresh();
        },
        async onCopyText(text, label = "Text") {
            if (!text) return;
            try {
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const input = document.createElement("textarea");
                    input.style = "position: absolute; left: -1000px; top: -1000px";
                    input.value = text;
                    document.body.appendChild(input);
                    input.select();
                    input.setSelectionRange(0, 99999);
                    document.execCommand("copy");
                    document.body.removeChild(input);
                }
                this.$store.dispatch("addToast", {
                    message: `${label} copied to clipboard.`,
                    type: "info",
                });
            } catch (e) {
                this.$store.dispatch("addToast", {
                    message: `Failed to copy ${label}.`,
                    type: "danger",
                });
            }
        },
    },
    computed: {
        domain() {
            return this.modalData?.domain || null;
        },
        details() {
            return this.modalData?.validationDetails || null;
        },
        edgeHostname() {
            return this.modalData?.edgeHostname || null;
        },
        aliasRecordLeft() {
            if (this.modalData?.aliasRecordLeft != null) return this.modalData.aliasRecordLeft;
            const combined = this.modalData?.aliasRecordValue;
            if (combined && typeof combined === 'string') {
                const i = combined.indexOf(' → ');
                return i >= 0 ? combined.slice(0, i).trim() : combined;
            }
            return null;
        },
        aliasRecordRight() {
            if (this.modalData?.aliasRecordRight != null) return this.modalData.aliasRecordRight;
            const combined = this.modalData?.aliasRecordValue;
            if (combined && typeof combined === 'string') {
                const i = combined.indexOf(' → ');
                return i >= 0 ? combined.slice(i + 3).trim() : null;
            }
            return null;
        },
        cnameRecordLeft() {
            if (this.modalData?.cnameRecordLeft != null) return this.modalData.cnameRecordLeft;
            const combined = this.modalData?.cnameRecordValue;
            if (combined && typeof combined === 'string') {
                const i = combined.indexOf(' → ');
                return i >= 0 ? combined.slice(0, i).trim() : combined;
            }
            return null;
        },
        cnameRecordRight() {
            if (this.modalData?.cnameRecordRight != null) return this.modalData.cnameRecordRight;
            const combined = this.modalData?.cnameRecordValue;
            if (combined && typeof combined === 'string') {
                const i = combined.indexOf(' → ');
                return i >= 0 ? combined.slice(i + 3).trim() : null;
            }
            return null;
        },
        showAliasRecord() {
            if (this.recordType === 'FWD') return false;
            return Boolean(this.modalData?.showAliasRecord);
        },
        showCnameRecord() {
            return this.modalData?.showCnameRecord !== false;
        },
        recordType() {
            return this.modalData?.recordType || null;
        },
        hasDnsSettings() {
            return Boolean(
                this.edgeHostname ||
                (this.showAliasRecord && (this.aliasRecordLeft || this.aliasRecordRight)) ||
                (this.showCnameRecord && (this.cnameRecordLeft || this.cnameRecordRight))
            );
        },
        cnameHost() {
            return this.details?.cname_record_host || null;
        },
        cnameTarget() {
            return this.details?.cname_record_target || null;
        },
        txtHost() {
            return this.details?.txt_record_host || null;
        },
        txtValue() {
            return this.details?.txt_record_value || null;
        },
        validationExpiry() {
            return this.details?.validation_expiry || null;
        },
        isValidationExpired() {
            const expiry = this.validationExpiry;
            if (!expiry) return false;
            try {
                const expiryDate = new Date(expiry);
                return isFinite(expiryDate.getTime()) && expiryDate < new Date();
            } catch {
                return false;
            }
        },
        hasAnyDetails() {
            return Boolean(
                this.validationExpiry ||
                this.cnameHost ||
                this.cnameTarget ||
                this.txtHost ||
                this.txtValue
            );
        },
    },
};
</script>

<style scoped>
.cname-validation-modal {
    border: none;
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.cname-validation-modal .card-header {
    padding: 0.5rem 1rem;
    flex-shrink: 0;
}

.cname-validation-modal .card-header-title {
    font-size: 1rem;
}

.cname-body {
    padding: 0.75rem 1rem;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
}

/* Sections */
.cname-section {
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    background: rgba(0, 123, 255, 0.1);
    border-left: 4px solid var(--bs-primary);
}

.cname-section-validation {
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    background: rgba(0, 255, 17, 0.1);
    border-left: 4px solid var(--bs-success);
}

.cname-section-option {
    background: #fff;
    border-color: #e2e8f0;
}

.cname-section-title {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.2rem;
}

.cname-section-desc {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.5rem;
}

.cname-field-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 0.2rem;
}

.cname-field {
    margin-bottom: 0;
}

.cname-field:last-child {
    margin-bottom: 0;
}

/* Tip box */
.cname-tip {
    font-size: 0.8125rem;
    color: #475569;
    padding: 0.4rem 0.6rem;
    border-radius: 6px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
}

.cname-tip strong {
    color: #1e40af;
}

/* Option blocks */
.cname-option-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.35rem;
}

.cname-option-badge {
    font-size: 0.6875rem;
    font-weight: 600;
    color: #0369a1;
    background: #e0f2fe;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
}

.cname-option-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #0f172a;
}

/* Input groups */
.cname-input-group .form-control {
    border-radius: 6px;
    border-color: #e2e8f0;
    background: #fff;
    font-size: 0.8125rem;
}

.cname-input-group .form-control:focus {
    border-color: #94a3b8;
    box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.2);
}

.cname-input-group .btn-copy {
    border-radius: 0 6px 6px 0;
    border-color: #e2e8f0;
    background: white;
    color: #475569;
    min-width: 2rem;
    padding: 0.25rem 0.4rem;
}

.cname-input-group .btn-copy:hover:not(:disabled) {
    background: #e2e8f0;
    color: #0f172a;
    border-color: #cbd5e1;
}

.cname-input-group .btn-copy:disabled {
    opacity: 0.5;
}

/* Arrow between columns */
.cname-arrow {
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 31px;
}

.cname-arrow .fa-arrow-right,
.cname-arrow .fa-arrow-down {
    font-size: 0.75rem;
}

/* Expiry */
.cname-expiry {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.4rem 0.6rem;
    border-radius: 6px;
    background: white;
    border: 1px solid #e2e8f0;
}

.cname-expiry-label {
    font-size: 0.75rem;
    color: #64748b;
}

.cname-expiry-value {
    font-size: 0.8125rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #0f172a;
}

.btn-copy-sm {
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
    border-radius: 4px;
    border-color: #e2e8f0;
    background: #fff;
    color: #475569;
}

.btn-copy-sm:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.btn-refresh-sm {
    padding: 0.2rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 4px;
    background: #0d6efd;
    border-color: #0d6efd;
    color: #fff;
}

.btn-refresh-sm:hover {
    background: #0b5ed7;
    border-color: #0a58ca;
    color: #fff;
}

/* Empty state */
.cname-empty {
    text-align: center;
    padding: 1rem 0.75rem;
    color: #94a3b8;
    font-size: 0.8125rem;
}

.cname-empty .fa-inbox {
    font-size: 1.5rem;
    opacity: 0.6;
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {

    .cname-section-desc,
    .cname-field-label {
        color: $dark-text-secondary;
    }

    .cname-input-group .btn-copy {
        border-radius: 0 6px 6px 0;
        border-color: $dark-border-color;
        background: $dark-elevated-bg;
        color: $dark-text-secondary;
    }

    .cname-input-group .btn-copy:hover:not(:disabled) {
        background: $dark-border-color;
        color: $dark-text-primary;
        border-color: $dark-border-color;
    }

    .cname-option-badge {
        color: $dark-accent;
        background: $dark-card-bg;
    }

    .cname-option-title {
        color: $dark-text-primary;
    }

    .cname-expiry {
        background: $dark-card-bg;
        border: 1px solid $dark-border-color;
    }

    .cname-expiry-label {
        color: $dark-text-secondary;
    }

    .cname-expiry-value {
        color: $dark-text-primary;
    }

}
</style>
